/**
 * 🎯 SA ポータル Hub (v3.3 IA 刷新 — カードグリッド UI)
 *
 * 旧 sa.* 7 個の独立メニューをカード型ランディングに集約。
 * ・SA (外部) が一目で全機能を把握できるよう、タブよりカードを採用。
 * ・各カードをクリック → 対応する個別ページに遷移。
 * ・本ファイルは「玄関」役。実 UI は個別の SaDashboardPage 等が担う。
 */
import {
  BookOpen,
  Calculator,
  ClipboardList,
  Kanban,
  LayoutDashboard,
  Presentation,
  Tag,
  UserCog,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { useMyPermissions } from '@/features/permissions';
import { hasPermission } from '@goenhub/shared';

interface SaCardSpec {
  featureId: string;
  path: string;
  icon: LucideIcon;
  title: string;
  description: string;
  accent: string;
}

const CARDS: SaCardSpec[] = [
  {
    featureId: 'sa.dashboard',
    path: '/sa/dashboard',
    icon: LayoutDashboard,
    title: 'ダッシュボード',
    description: '担当商談の集計・進捗・成績サマリーを一目で確認',
    accent: 'var(--accent)',
  },
  {
    featureId: 'sa.contacts',
    path: '/sa/contacts',
    icon: UserRound,
    title: '自分の顧客',
    description: '担当顧客の一覧 / 新規追加 / 紐付け確認',
    accent: 'var(--accent-cyan)',
  },
  {
    featureId: 'sa.deals',
    path: '/sa/deals',
    icon: Kanban,
    title: '担当商談',
    description: '自分が担当する商談カードを Stage 別に管理 / 新規商談起票',
    accent: 'var(--accent-purple)',
  },
  {
    featureId: 'sa.briefings',
    path: '/briefings',
    icon: Presentation,
    title: '個別説明',
    description: '自分が担当する個別説明の予定 / 記録の保存',
    accent: 'var(--accent)',
  },
  {
    featureId: 'sa.forms',
    path: '/sa/forms',
    icon: ClipboardList,
    title: 'フォーム',
    description: '個別説明 / 見積 / 資料請求の専用 URL を発行',
    accent: 'var(--accent-cyan)',
  },
  {
    featureId: 'sa.catalogs',
    path: '/sa/catalogs',
    icon: Tag,
    title: '価格表',
    description: '商品・セット商品の最新価格を参照',
    accent: 'var(--accent)',
  },
  {
    featureId: 'sa.documents',
    path: '/sa/documents',
    icon: BookOpen,
    title: '資料',
    description: '商品説明資料・提案書のテンプレート集',
    accent: 'var(--accent-purple)',
  },
  {
    featureId: 'sa.commissions',
    path: '/sa/commissions',
    icon: Calculator,
    title: '報酬計算',
    description: '商品・数量・割引から想定報酬を即時シミュレーション',
    accent: 'var(--accent-cyan)',
  },
  {
    featureId: 'sa.profile',
    path: '/sa/profile',
    icon: UserCog,
    title: 'プロフィール',
    description: '基本情報・振込先・SNS リンクを管理',
    accent: 'var(--accent)',
  },
];

export function SaHubPage() {
  const navigate = useNavigate();
  const { isOwner, permissions } = useMyPermissions();

  const visible = CARDS.filter((c) =>
    hasPermission({ isOwner, permissions, featureId: c.featureId, action: 'view' }),
  );

  if (visible.length === 0) {
    return (
      <Screen title="SA ポータル">
        <div className="text-sm text-[var(--text-muted)] p-8 text-center">
          SA ポータルへのアクセス権限がありません。
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      title="SA ポータル"
      description="セールスエージェント向けのワークスペース。日々の活動に必要な機能をカードから直接開けます。"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.featureId}
              type="button"
              onClick={() => navigate(card.path)}
              className="group text-left rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] hover:border-[var(--accent)] hover:shadow-md transition-all p-5 flex gap-4 items-start"
            >
              <div
                className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
                style={{
                  backgroundColor: `color-mix(in srgb, ${card.accent} 16%, transparent)`,
                  color: card.accent,
                }}
              >
                <Icon className="size-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[15px] text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors mb-1.5">
                  {card.title}
                </h3>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                  {card.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </Screen>
  );
}
